import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import './index.css';
import Poster from './components/Poster';
import EventConfig from './components/EventConfig';
import ImportPdfs from './components/ImportPdfs';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { Row, Col ,Container} from 'react-bootstrap';
import { Multiselect } from 'multiselect-react-dropdown';
import { API_URL, IMAGE_HANDLER_URL, TRANSLATIONS_ENDPOINT } from './const';

// Detectar automáticamente si estamos en Electron
const USE_ONLINE = !window.electronAPI;
const DEFAULT_OFFLINE_LANGUAGE = 'es';

import './i18n';
import i18n, { loadCustomTranslations } from './i18n';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { insertCss, transformTranslations } from './Utils';

function App() {
  const { t, i18n } = useTranslation();
  const [posters, setPosters] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [types, setTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [pictureBackground, setPictureBackground] = useState([]);
  const [pictureHeader, setPictureHeader] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0, title: '' });
  const [translationsLoaded, setTranslationsLoaded] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showImportPdfsModal, setShowImportPdfsModal] = useState(false);
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1];

  // Redirección para /offline (solo en modo online)
  useEffect(() => {
    if (USE_ONLINE && (window.location.pathname === '/offline' || window.location.pathname.endsWith('/offline'))) {
      window.location.href = 'https://img.kingconf.com/static/eposterInstaller.zip';
    }
  }, []);

  const dataUpdate = function(result){
      setPosters(result.posters.filter(p => p.deleted != 1).sort((a,b)=>a.title.localeCompare(b.title)));
      setTracks(result.tracks);
      setTypes(result.types);
      setRooms(result.rooms || []);
  }

  useEffect(() => {
    const loadCss = async () => {
      // Cargar principal.css primero (con imágenes en base64)
      if (window?.electronAPI?.getPrincipalCssPath) {
        try {
          const principalPath = await window.electronAPI.getPrincipalCssPath();
          if (principalPath) {
            const principalHref = `file://${principalPath.replace(/\\\\/g, '/')}`;
            const principalLink = document.createElement('link');
            principalLink.rel = 'stylesheet';
            principalLink.href = principalHref;
            principalLink.id = 'principal-css';
            principalLink.onerror = () => console.log('⚠️ principal.css no encontrado (se generará al descargar posters)');
            document.head.appendChild(principalLink);
            console.log('CSS principal cargado');
          } else {
            console.log('⚠️ principal.css no existe todavía, se creará al descargar posters');
          }
        } catch (error) {
          console.log('⚠️ Error al cargar principal.css:', error);
        }
      }

      // Luego cargar custom.css (personalizaciones del usuario)
      if (window?.electronAPI?.getCustomCssPath) {
        try {
          const rawPath = await window.electronAPI.getCustomCssPath();
          if (rawPath) {
            const href = `file://${rawPath.replace(/\\\\/g, '/')}`; // para Windows
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            link.id = 'custom-css';
            link.onerror = () => console.log('⚠️ custom.css no encontrado');
            console.log('CSS custom cargado')
            document.head.appendChild(link);
          } else {
            console.log('⚠️ custom.css no existe todavía');
          }
        } catch (error) {
          console.log('⚠️ Error al cargar custom.css:', error);
        }
      }
    };

    const loadTranslations = async (eventId) => {
      console.log(`🌐 Cargando traducciones. USE_ONLINE: ${USE_ONLINE}, eventId: ${eventId}`);

      if (USE_ONLINE && eventId) {
        try {
          const endpoint = TRANSLATIONS_ENDPOINT(eventId);
          const res = await axios.get(endpoint);

          // Transformar traducciones del backend al formato i18n
          const transformedTranslations = transformTranslations(res.data);
          loadCustomTranslations(transformedTranslations);

          // Forzar re-render después de cargar traducciones
          setTranslationsLoaded(true);
        } catch (error) {
          console.warn("⚠️ No se pudieron cargar traducciones online:", error.message);
        }
      } else if (window?.electronAPI?.getCustomTranslations) {
        try {
          console.log("📥 Intentando cargar traducciones desde archivo local...");
          const customTranslations = await window.electronAPI.getCustomTranslations();
          console.log("📦 Traducciones recibidas:", customTranslations);

          if (customTranslations) {
            loadCustomTranslations(customTranslations);
            // Forzar re-render después de cargar traducciones
            setTranslationsLoaded(true);
            console.log("✅ Traducciones personalizadas cargadas");
          } else {
            console.warn("⚠️ No se encontraron traducciones guardadas");
          }
        } catch (error) {
          console.warn("⚠️ No se pudieron cargar traducciones guardadas:", error.message);
        }
      } else {
        console.log("⚠️ No se puede cargar traducciones: condiciones no cumplidas");
      }
    };




    const loadData = async () => {
      if (USE_ONLINE) {
        try {
          if (!lastSegment) {
            console.error("No se pudo obtener el segmento de la URL");
            return;
          }  
       

          const endpoint = `${API_URL}${encodeURIComponent(lastSegment)}/eposters`;
          const res = await axios.get(endpoint);
          i18n.changeLanguage(res.data.eventModel.eventModelV3.mainLanguage?.toLowerCase());
          const model = res.data.eventModel;
          const eventId = res.data.eventModel.eventModelV3.id;

          setPosters(model.sessionModelList.filter(p => p.deleted != 1).sort((a,b)=>a.title.localeCompare(b.title)));
          setTracks(model.sessionTrackModelList);
          setTypes(model.sessionTypeModelList);
          console.log(model.sessionTypeModelList);
          setRooms(model.roomModelList || []);
          setPictureBackground(IMAGE_HANDLER_URL+res.data.idPictureBackground);
          setPictureHeader(IMAGE_HANDLER_URL+res.data.idPictureHeader);
          insertCss(res.data.cssStyles);

          // Cargar traducciones usando el ID del evento
          loadTranslations(eventId);
          
        } catch (error) {
          console.error("❌ Error cargando datos online:", error);
        }
      } 
    };
    if (typeof window !== 'undefined' && window.electronAPI) {
      window.electronAPI.getSavedData().then((result)=> {
        console.log("📦 Datos guardados:", result);
        if (result) {
          dataUpdate(result);
          const lang = result.mainLanguage || DEFAULT_OFFLINE_LANGUAGE;
          i18n.changeLanguage(lang);
          console.log(`🌐 Idioma configurado desde eventPackage: ${lang}`);
        } else {
          console.warn("⚠️ No hay datos guardados");
          i18n.changeLanguage(DEFAULT_OFFLINE_LANGUAGE);
        }
      }).catch(err => {
        console.error("❌ Error al invocar:", err);
      });
      window.electronAPI.onPostersUpdating(() => {
        console.log("🔄 Iniciando actualización de posters");
        setIsUpdating(true);
      });

      window.electronAPI.onPostersUpdateError((msg) => {
        console.error("❌ Error actualizando:", msg);
        setIsUpdating(false);
      });

      // Cuando los datos están listos, actualizar UI pero mantener el loading
      window.electronAPI.onPostersDataReady((result) => {
        console.log("✅ Datos listos, actualizando UI");
        dataUpdate(result);
      });

      // Cuando TODO termina (incluyendo PDFs), quitar loading
      window.electronAPI.onPostersUpdated(() => {
        console.log("🎉 Actualización completada");
        setIsUpdating(false);
        setProgress({ current: 0, total: 0, title: '' });
        window.electronAPI.getSavedData().then((result)=> {
          dataUpdate(result);
        }).catch(err => {
          console.error("❌ Error al invocar:", err);
        });
      });

      window.electronAPI.onPosterProgress((data) => {
        console.log(`📥 Progreso: ${data.current}/${data.total}`);
        setProgress(data);
      });

      // Función para cargar imágenes locales
      const loadLocalImages = () => {
        window.electronAPI.getLocalPath().then((localPath) => {
          // Agregar timestamp para forzar recarga y evitar cache
          const timestamp = new Date().getTime();
          const bgPath = `file:///${localPath}/background-body.jpg?t=${timestamp}`.replace(/\\/g, '/');
          const headerPath = `file:///${localPath}/background-header.jpg?t=${timestamp}`.replace(/\\/g, '/');
          setPictureBackground(bgPath);
          setPictureHeader(headerPath);
          console.log('🖼️ Imágenes cargadas:', { bgPath, headerPath });
        });
      };

      // Cargar imágenes locales al inicio
      loadLocalImages();

      // Recargar imágenes cuando se actualicen
      window.electronAPI.onImagesUpdated(() => {
        console.log('🔄 Recargando imágenes actualizadas...');
        loadLocalImages();
      });

      // Recargar CSS cuando se actualice
      window.electronAPI.onCssUpdated((cssContent) => {
        console.log('🎨 Aplicando CSS actualizado...');
        // Remover CSS anterior si existe
        const oldCustomCss = document.getElementById('custom-css');
        if (oldCustomCss) {
          oldCustomCss.remove();
        }
        // Aplicar nuevo CSS
        insertCss(cssContent);
      });

      // Cargar traducciones personalizadas offline
      loadTranslations(null);
      
    }
    loadData();
    loadCss();
  }, []);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleTrackFilterSelect = (selectedList) => {
    setSelectedTracks(selectedList.map(t => Number(t.id)));
  };
  const handleTypeFilterSelect = (selectedList) => {
    setSelectedTypes(selectedList.map(t => Number(t.id)));
  };


  const matchesSearch = (p) =>
    p.title?.toLowerCase().includes(searchQuery) ||
    p.speakerNamesConcatenated?.toLowerCase().includes(searchQuery) ||
    p.allAuthorsText?.toLowerCase().includes(searchQuery) ||
    p.description?.toLowerCase().includes(searchQuery);
  const matchesTrack = (p) => selectedTracks.length === 0 || selectedTracks.includes(p.idActivityTrack);
  const matchesTypes = (p) => selectedTypes.length === 0 || selectedTypes.includes(p.idActivityType);
  const matchesRoom = (p) => !selectedRoom || p.idActivityRoom === Number(selectedRoom);

  const filteredPosters = posters.filter(p =>
    matchesSearch(p) && matchesTrack(p) && matchesTypes(p) && matchesRoom(p)
  );

  const availableRoomIds = new Set(
    posters
      .filter(p => matchesSearch(p) && matchesTrack(p) && matchesTypes(p))
      .map(p => String(p.idActivityRoom))
  );
  const availableRooms = rooms.filter(r => availableRoomIds.has(String(r.id)));
  

  // Exponer función para abrir modal desde Electron
  useEffect(() => {
    if (window.electronAPI) {
      window.openConfigModal = () => setShowConfigModal(true);
      window.openImportPdfsModal = () => setShowImportPdfsModal(true);
    }
  }, []);

  return (
    <Router>
        <EventConfig show={showConfigModal} onClose={() => setShowConfigModal(false)} />
        <ImportPdfs show={showImportPdfsModal} onClose={() => setShowImportPdfsModal(false)} />

        {isUpdating && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <p>📥 Actualizando posters...</p>
            <p>{progress.title}</p>
            <p>{progress.current} de {progress.total}</p>
          </div>
        </div>
      )}
      <div className="App">
        <div className='header-image' style={{ backgroundImage: `url(${pictureHeader})`}}>

        </div>
        <div className='body-wrapper-background' style={{ backgroundImage: `url(${pictureBackground})`}}>
          <div className='body-wrapper'>
            <div className='search-box'>
              <Row>
                  <Col sm={12} id="PosterSearchGenericPanel">
                <h3>{t('search')}</h3>
                    <input
                      type="text"
                      className="form-control poster-input-track-filter search-input"
                      placeholder={t('typeHere')}
                      onChange={handleSearchChange}
                      value={searchQuery}
                    />

                </Col>
              </Row>
              <Row>
              
                {tracks &&(
                  <Col  sm={4} id="PosterFilterBytopic">
                  <h3>{t('Filter by topic')}</h3>
                    <Multiselect
                        options={tracks.sort((a, b) => {
                          const orderDiff = (a.order || 0) - (b.order || 0);
                          return orderDiff !== 0 ? orderDiff : a.description.localeCompare(b.description);
                        })}
                        displayValue="description"
                        placeholder={t('topic')}
                        onSelect={handleTrackFilterSelect}
                        onRemove={handleTrackFilterSelect}
                        avoidHighlightFirstOption={true}
                        hidePlaceholder={true}
                        emptyRecordMsg={t('noOptionsAvailable')}
                      />
                </Col>
                )}
                {types &&(
                <Col  sm={4} id="PosterFilterByType">
                  <h3>{t('Filter by type')}</h3>
                    <Multiselect
                        options={types.sort((a, b) => {
                          const orderDiff = (a.order || 0) - (b.order || 0);
                          return orderDiff !== 0 ? orderDiff : a.description.localeCompare(b.description);
                        })}
                        displayValue="description"
                        placeholder={t('type')}
                        onSelect={handleTypeFilterSelect}
                        onRemove={handleTypeFilterSelect}
                        avoidHighlightFirstOption={true}
                        hidePlaceholder={true}
                        emptyRecordMsg={t('noOptionsAvailable')}
                      />
                </Col>
                )}
                {rooms &&(
                <Col  sm={4} id="PosterFilterByRoom">
                  <h3>{t('Presented at')}</h3>
                  <div className="custom-select-wrapper">
                    <select
                      className="form-select custom-select"
                      value={selectedRoom}
                      onChange={(e) => setSelectedRoom(Number(e.target.value))}
                    >
                      <option value="">{t('allRooms')}</option>
                      {availableRooms?.sort((a, b) => {
                        const orderDiff = (a.order || 0) - (b.order || 0);
                        return orderDiff !== 0 ? orderDiff : a.name.localeCompare(b.name);
                      }).map(room => (
                        <option key={room.id} value={room.id}>
                          {room.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>
                )}
              </Row>
            </div>
            {filteredPosters.map(p => <Poster key={p.id} poster={p} />)}
          </div>
        </div>

      </div>
    </Router>
  );
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(<App />);